import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms, SAFE_ZONE_VERTEX_CHUNK } from '../shaders/safeZone.glsl.js';
import { getArcade, preloadArcade } from '../arcade-assets.js';
import { artProxyUrl } from '../artwork-texture.js';

// Arcade - a dark retro arcade at night, from two photographs of the same
// room: everything off, and screens + marquees on. Every screen plays a
// mini-game built from the cover, the marquees light to the beat, and on
// the drop the whole room plays the same game. Chiptune, synthwave,
// electro, electronic pop, hip-hop.
//
// THE DECISIVE DETAIL: the screens. tools/build-arcade.mjs fitted a
// quadrilateral to every lit screen in the difference of the two pictures
// and turned it into a HOMOGRAPHY. For a screen pixel, the inverse
// homography gives its (u, v) inside the screen, and the content is read
// there in an ATLAS OF GAMES: one render target, 4 x 4 cells of 256 x 192,
// rendered once per frame in one pass, each cell one game. The content is
// also the light: the indirect light a screen throws on its cabinet takes
// the mean colour of its cell (a 4 x 4 "mipmap" of the atlas, rendered by
// hand, since a sampler cannot be asked for a level in WebGL1).
//
// The games are AUTOMATA DRIVEN BY MUSICAL TIME, not simulations: the ball
// bounces on a trajectory computed from the bar's phase, the enemies step
// down once per beat, the snake eats one pixel per beat. Everything in
// pixel art, from the cover quantised to sixteen colours.

const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'arcade',
  family: 'photographic',
  energyLevel: 0.70,
  bpmMin: 100,
  bpmMax: 160,
  movement: 0.65,
  danceability: 0.76,
  tonality: 0.60,
  prefers: { gridStrength: [0.55, 1.0] },
  enabled: true
};

const DEFAULT_TIER = 2;
const TIER = {
  2: { atlas: [1024, 768], persistence: true, every: 1 },
  1: { atlas: [512, 384], persistence: false, every: 1 },
  0: { atlas: [512, 384], persistence: false, every: 2 }
};
const CELLS = 4;                 // 4 x 4 cells
const MAX_SOURCES = 32;          // screens + marquees, homographies in a texture
const SAFE_FLOOR = 0.5;

const MODE_BRICKS = 0, MODE_SHOOTER = 1, MODE_SNAKE = 2, MODE_BLOCKS = 3, MODE_PORTRAIT = 4, MODE_ATTRACT = 5, MODE_SYNC = 6, MODE_PAUSED = 7;
const MODE_NAMES = { bricks: MODE_BRICKS, shooter: MODE_SHOOTER, snake: MODE_SNAKE, blocks: MODE_BLOCKS, portrait: MODE_PORTRAIT, attract: MODE_ATTRACT };
const GAME_POOL = [MODE_BRICKS, MODE_SHOOTER, MODE_SNAKE, MODE_BLOCKS, MODE_PORTRAIT, MODE_BRICKS, MODE_SHOOTER, MODE_SNAKE, MODE_BLOCKS, MODE_ATTRACT];
const DROP_BARS = 4;
const PALETTE_SIZE = 16;
const COVER_PX = 64;

// Text strips in the text canvas (16 strips of 64 px in 1024 x 1024):
const STRIP_TITLE = 0, STRIP_COIN = 1, STRIP_OVER = 2, STRIP_LEVEL = 3, STRIP_PAUSED = 4, STRIP_NAMES = 5;
const WORDS_A = ['NEON', 'HYPER', 'MEGA', 'ULTRA', 'STAR', 'TURBO', 'ROBO', 'LASER', 'PIXEL', 'GALAXY', 'CYBER', 'ASTRO', 'DISCO', 'METEOR', 'NOVA', 'ZERO', 'MAGNET', 'VECTOR'];
const WORDS_B = ['FIGHTER', 'QUEST', 'STRIKE', 'RUNNER', 'BLASTER', 'PATROL', 'FORCE', 'DRIFT', 'RALLY', 'MANIA', 'SQUAD', 'ARENA', 'STORM', 'RIDER', 'JUMPER'];

// A 5 x 7 pixel font: each glyph is seven rows of five bits, top row first.
const FONT = {
  A: [0x0E, 0x11, 0x11, 0x1F, 0x11, 0x11, 0x11], B: [0x1E, 0x11, 0x11, 0x1E, 0x11, 0x11, 0x1E], C: [0x0E, 0x11, 0x10, 0x10, 0x10, 0x11, 0x0E],
  D: [0x1C, 0x12, 0x11, 0x11, 0x11, 0x12, 0x1C], E: [0x1F, 0x10, 0x10, 0x1E, 0x10, 0x10, 0x1F], F: [0x1F, 0x10, 0x10, 0x1E, 0x10, 0x10, 0x10],
  G: [0x0E, 0x11, 0x10, 0x17, 0x11, 0x11, 0x0F], H: [0x11, 0x11, 0x11, 0x1F, 0x11, 0x11, 0x11], I: [0x0E, 0x04, 0x04, 0x04, 0x04, 0x04, 0x0E],
  J: [0x07, 0x02, 0x02, 0x02, 0x02, 0x12, 0x0C], K: [0x11, 0x12, 0x14, 0x18, 0x14, 0x12, 0x11], L: [0x10, 0x10, 0x10, 0x10, 0x10, 0x10, 0x1F],
  M: [0x11, 0x1B, 0x15, 0x15, 0x11, 0x11, 0x11], N: [0x11, 0x11, 0x19, 0x15, 0x13, 0x11, 0x11], O: [0x0E, 0x11, 0x11, 0x11, 0x11, 0x11, 0x0E],
  P: [0x1E, 0x11, 0x11, 0x1E, 0x10, 0x10, 0x10], Q: [0x0E, 0x11, 0x11, 0x11, 0x15, 0x12, 0x0D], R: [0x1E, 0x11, 0x11, 0x1E, 0x14, 0x12, 0x11],
  S: [0x0F, 0x10, 0x10, 0x0E, 0x01, 0x01, 0x1E], T: [0x1F, 0x04, 0x04, 0x04, 0x04, 0x04, 0x04], U: [0x11, 0x11, 0x11, 0x11, 0x11, 0x11, 0x0E],
  V: [0x11, 0x11, 0x11, 0x11, 0x11, 0x0A, 0x04], W: [0x11, 0x11, 0x11, 0x15, 0x15, 0x15, 0x0A], X: [0x11, 0x11, 0x0A, 0x04, 0x0A, 0x11, 0x11],
  Y: [0x11, 0x11, 0x11, 0x0A, 0x04, 0x04, 0x04], Z: [0x1F, 0x01, 0x02, 0x04, 0x08, 0x10, 0x1F],
  '0': [0x0E, 0x11, 0x13, 0x15, 0x19, 0x11, 0x0E], '1': [0x04, 0x0C, 0x04, 0x04, 0x04, 0x04, 0x0E], '2': [0x0E, 0x11, 0x01, 0x02, 0x04, 0x08, 0x1F],
  '3': [0x1F, 0x02, 0x04, 0x02, 0x01, 0x11, 0x0E], '4': [0x02, 0x06, 0x0A, 0x12, 0x1F, 0x02, 0x02], '5': [0x1F, 0x10, 0x1E, 0x01, 0x01, 0x11, 0x0E],
  '6': [0x06, 0x08, 0x10, 0x1E, 0x11, 0x11, 0x0E], '7': [0x1F, 0x01, 0x02, 0x04, 0x08, 0x08, 0x08], '8': [0x0E, 0x11, 0x11, 0x0E, 0x11, 0x11, 0x0E],
  '9': [0x0E, 0x11, 0x11, 0x0F, 0x01, 0x02, 0x0C], '-': [0x00, 0x00, 0x00, 0x1F, 0x00, 0x00, 0x00], '!': [0x04, 0x04, 0x04, 0x04, 0x04, 0x00, 0x04],
  '.': [0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x04], "'": [0x04, 0x04, 0x00, 0x00, 0x00, 0x00, 0x00], ' ': [0, 0, 0, 0, 0, 0, 0]
};

const SYNTH_BASE = 0.10, SYNTH_DECAY = 1.9;
const SYNTH_PERIOD_LOW = 4.7, SYNTH_PERIOD_MID = 3.1, SYNTH_PERIOD_HIGH = 6.3;
const SYNTH_BPM = 124;
const FALLBACK_COLORS = [0xff4d8a, 0x40d0ff, 0xffd84a, 0x7cff6b, 0xc084ff, 0xff8a3d];

// =============================================================================
// SHADERS
// =============================================================================

const PASS_VERTEX = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }
`;

// The atlas: 4 x 4 cells, one game per cell, 64 x 48 "pixels" per game.
// Every game is a function of the musical counters (uBeat, uBar and their
// phases) and of the cell's own seed: nothing is simulated, nothing drifts.
const ATLAS_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uCells;      // 16 x 1: R mode, G seed, B beat offset, A unused
  uniform sampler2D uCover;      // the cover, quantised, 64 x 64
  uniform sampler2D uPalette;    // 16 x 1
  uniform sampler2D uText;       // 16 strips of text, alpha in r
  uniform float uBeat;           // beat counter, bounded
  uniform float uBeatPhase;
  uniform float uBar;
  uniform float uBarPhase;
  uniform float uBlink;          // 0/1 at a half-beat rate
  uniform float uDropBar;        // bars since the drop, or -1
  uniform float uHasText;
  varying vec2 vUv;

  float hash(vec2 p) {
    p = fract(p * vec2(0.3183, 0.3679)) + vec2(0.71, 0.113);
    p += dot(p, p.yx + 1.9);
    return fract(p.x * p.y * 7.31);
  }
  vec3 pal(float i) { return texture2D(uPalette, vec2((mod(i, 16.0) + 0.5) / 16.0, 0.5)).rgb; }
  vec3 cover(vec2 q) { return texture2D(uCover, clamp(q, 0.0, 1.0)).rgb; }
  float tri(float t) { return abs(fract(t) * 2.0 - 1.0); }
  float inBox(vec2 p, vec2 a, vec2 b) { return step(a.x, p.x) * step(p.x, b.x) * step(a.y, p.y) * step(p.y, b.y); }
  // text strip s sampled at (x in 0..1 across, y in 0..1 up)
  float text(float s, vec2 xy) {
    if (uHasText < 0.5) return 0.0;
    return texture2D(uText, vec2(xy.x, (s + 1.0 - clamp(xy.y, 0.0, 1.0)) / 16.0)).r;
  }
  float textHalf(float s, float hf, vec2 xy) {
    if (uHasText < 0.5) return 0.0;
    return texture2D(uText, vec2(hf * 0.5 + xy.x * 0.5, (s + 1.0 - clamp(xy.y, 0.0, 1.0)) / 16.0)).r;
  }

  vec3 bricks(vec2 p, vec2 local, float seed, float beat) {
    // bricks: a 16 x 8 cover, 4 x 2 px each, rows 30..45; broken in a
    // seeded order as the beats pass
    vec3 c = vec3(0.0);
    float prog = fract(beat / 64.0);
    if (p.y >= 30.0 && p.y < 46.0) {
      float bi = floor(p.x / 4.0), bj = floor((p.y - 30.0) / 2.0);
      float broken = step(hash(vec2(bi * 0.07 + seed, bj * 0.13)), prog);
      vec3 bc = cover(vec2((bi + 0.5) / 16.0, (bj + 0.5) / 8.0));
      float gap = step(mod(p.x, 4.0), 2.5) * step(mod(p.y, 2.0), 0.5) * 0.0 + 1.0;
      c = bc * (1.0 - broken) * gap;
      if (mod(p.x, 4.0) > 2.5) c *= 0.55;
    }
    // the ball: one bounce per beat between the paddle and the bricks,
    // one sweep across per two bars
    float bx = 3.0 + 58.0 * tri((uBar + uBarPhase) * 0.5 + seed);
    float by = 4.0 + 25.0 * tri((beat + uBeatPhase) * 0.5);
    c += vec3(1.0) * inBox(p, vec2(bx - 1.0, by - 1.0), vec2(bx, by));
    // the paddle under it
    c += pal(1.0) * inBox(p, vec2(bx - 5.0, 1.0), vec2(bx + 5.0, 2.0));
    return c;
  }

  vec3 shooter(vec2 p, vec2 local, float seed, float beat) {
    vec3 c = vec3(0.0);
    // stars scrolling down
    float col = floor(p.x / 2.0);
    float sy = mod(hash(vec2(col * 0.11, seed)) * 48.0 + (beat + uBeatPhase) * 3.0, 48.0);
    c += vec3(0.25) * step(abs(p.y - floor(sy)), 0.5) * step(hash(vec2(col * 0.37, seed + 0.5)), 0.35);
    // the formation: 8 x 3, 3 x 3 sprites, one step down per beat, a wobble every two
    float fall = mod(beat, 16.0);
    float wob = (mod(floor(beat / 2.0), 2.0) < 0.5) ? 0.0 : 2.0;
    for (int j = 0; j < 3; j++) for (int i = 0; i < 8; i++) {
      float ex = 6.0 + float(i) * 7.0 + wob, ey = 42.0 - float(j) * 5.0 - fall;
      vec2 d = p - vec2(ex, ey);
      if (d.x >= 0.0 && d.x < 3.0 && d.y >= 0.0 && d.y < 3.0) {
        float alive = step(hash(vec2(float(i) * 0.19 + seed, float(j) * 0.23)), 0.85);
        float px = step(0.3, hash(vec2(d.x * 0.3 + float(j), d.y * 0.3 + seed)));
        c += pal(3.0 + float(j)) * alive * (px * 0.6 + 0.4);
      }
    }
    // the ship, its shots on the off-beats
    float shx = 32.0 + 12.0 * sin((uBar + uBarPhase) * 3.1416 + seed * 6.0);
    c += pal(1.0) * inBox(p, vec2(shx - 2.0, 2.0), vec2(shx + 2.0, 3.0));
    c += vec3(1.0) * inBox(p, vec2(shx, 3.0), vec2(shx, 4.0));
    float sh = fract(uBeatPhase + 0.5);
    float bulletY = 5.0 + 38.0 * sh;
    c += vec3(1.0, 1.0, 0.6) * inBox(p, vec2(shx, bulletY), vec2(shx, bulletY + 2.0));
    return c;
  }

  vec3 snake(vec2 p, vec2 local, float seed, float beat) {
    // 16 x 12 cells; the snake follows a boustrophedon through the cover,
    // one cell per beat, eating the pixels it passes
    vec2 cell = floor(p / 4.0);
    float row = cell.y;
    float colk = mod(row, 2.0) < 0.5 ? cell.x : 15.0 - cell.x;
    float k = row * 16.0 + colk;
    float head = mod(beat + seed * 40.0, 192.0);
    float behind = head - k;
    vec3 c = vec3(0.0);
    vec2 inCell = mod(p, 4.0);
    float border = step(0.5, inCell.x) * step(inCell.x, 2.5) * step(0.5, inCell.y) * step(inCell.y, 2.5);
    if (k > head) c = cover(vec2((cell.x + 0.5) / 16.0, (cell.y + 0.5) / 12.0)) * (border * 0.4 + 0.6);
    else if (behind >= 0.0 && behind < 12.0) c = (behind < 1.0 ? vec3(1.0) : pal(1.0) * (1.0 - behind / 16.0)) * border;
    return c;
  }

  vec3 blocks(vec2 p, vec2 local, float seed, float beat) {
    // a stack that grows a row per bar, a 2 x 2 piece falling a row per beat
    vec2 cell = floor(p / 4.0);
    float stackH = mod(uBar + floor(seed * 6.0), 10.0);
    vec3 c = vec3(0.0);
    vec2 inCell = mod(p, 4.0);
    float bevel = 1.0 - 0.35 * step(2.5, inCell.x) - 0.25 * step(inCell.y, 0.5);
    if (cell.y < stackH) {
      float filled = step(0.2, hash(vec2(cell.x * 0.17 + seed, cell.y * 0.29)));
      c = pal(floor(hash(vec2(cell.x * 0.31, cell.y * 0.07 + seed)) * 6.0) + 2.0) * filled * bevel;
      // the completed line flashes at the bar
      if (cell.y == stackH - 1.0 && uBarPhase < 0.12) c = vec3(1.0) * filled;
    }
    float px = floor(hash(vec2(uBar * 0.13, seed)) * 14.0);
    float py = 10.0 - mod(beat, 8.0);
    if (cell.x >= px && cell.x < px + 2.0 && cell.y >= py && cell.y < py + 2.0) c = pal(5.0 + mod(uBar, 4.0)) * bevel;
    return c;
  }

  vec3 portrait(vec2 p, vec2 local, float seed, float beat, float full) {
    // the cover at 64 x 48, revealed three rows per beat over sixteen beats
    float revealed = full > 0.5 ? 48.0 : mod(beat, 16.0) * 3.0 + floor(uBeatPhase * 3.0);
    vec3 c = cover(vec2((p.x + 0.5) / 64.0, 0.125 + 0.75 * (p.y + 0.5) / 48.0));
    return c * step(p.y, revealed) * (1.0 - 0.5 * step(revealed - 1.0, p.y) * step(p.y, revealed));
  }

  vec3 attract(vec2 p, vec2 local, float seed, float paused) {
    vec3 c = vec3(0.0);
    // colour bars across the top
    if (p.y >= 42.0) c = pal(floor(p.x / 8.0)) * 0.8;
    // a slow starfield
    float col = floor(p.x / 3.0);
    float sy = mod(hash(vec2(col * 0.11, seed)) * 48.0 + uBar * 2.0 + uBarPhase * 2.0, 42.0);
    c += vec3(0.3) * step(abs(p.y - floor(sy)), 0.5) * step(hash(vec2(col * 0.37, 0.2)), 0.4);
    // the title, and INSERT COIN blinking
    float t = text(${STRIP_TITLE}.0, vec2(local.x, (local.y - 0.52) / 0.16));
    c = mix(c, pal(1.0) + vec3(0.3), t * step(0.52, local.y) * step(local.y, 0.68));
    float coin = text(paused > 0.5 ? ${STRIP_PAUSED}.0 : ${STRIP_COIN}.0, vec2(local.x, (local.y - 0.22) / 0.14));
    c = mix(c, vec3(1.0), coin * step(0.22, local.y) * step(local.y, 0.36) * (paused > 0.5 ? 1.0 : uBlink));
    return c;
  }

  void main() {
    vec2 cellIx = floor(vUv * ${CELLS}.0);
    float index = cellIx.y * ${CELLS}.0 + cellIx.x;
    vec2 local = fract(vUv * ${CELLS}.0);
    vec4 cd = texture2D(uCells, vec2((index + 0.5) / 16.0, 0.5));
    float mode = cd.r, seed = cd.g;
    float beat = uBeat + floor(cd.b * 32.0);
    vec2 p = floor(local * vec2(64.0, 48.0));
    vec3 c;
    if (mode < 0.5) c = bricks(p, local, seed, beat);
    else if (mode < 1.5) c = shooter(p, local, seed, beat);
    else if (mode < 2.5) c = snake(p, local, seed, beat);
    else if (mode < 3.5) c = blocks(p, local, seed, beat);
    else if (mode < 4.5) c = portrait(p, local, seed, beat, 0.0);
    else if (mode < 5.5) c = attract(p, local, seed, 0.0);
    else if (mode < 6.5) {
      // SYNC: the full portrait, then GAME OVER / LEVEL UP flashing by the bar
      c = portrait(p, local, seed, beat, 1.0);
      if (uDropBar >= 2.0) {
        float which = mod(floor(uDropBar), 2.0);
        float t = text(which < 0.5 ? ${STRIP_OVER}.0 : ${STRIP_LEVEL}.0, vec2(local.x, (local.y - 0.40) / 0.20));
        float flash = step(0.5, uBlink);
        c = mix(c * 0.35, vec3(1.0, 0.95, 0.6), t * step(0.40, local.y) * step(local.y, 0.60) * (0.5 + 0.5 * flash));
      }
    } else c = attract(p, local, seed, 1.0);
    // the phosphor ground: a dark blue-grey the sprites sit on
    c = max(c, pal(0.0) * 0.35 + vec3(0.05, 0.07, 0.11));
    gl_FragColor = vec4(c, 1.0);
  }
`;

// Phosphor persistence: the previous frame decays under the new one.
const PERSIST_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uFresh;
  uniform sampler2D uPrev;
  varying vec2 vUv;
  void main() {
    vec3 a = texture2D(uFresh, vUv).rgb, b = texture2D(uPrev, vUv).rgb;
    gl_FragColor = vec4(max(a, b * 0.62), 1.0);
  }
`;

// The 4 x 4 mean of the atlas, by hand: 48 taps per cell.
const MEAN_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uAtlas;
  varying vec2 vUv;
  void main() {
    vec2 cell = floor(vUv * ${CELLS}.0);
    vec3 s = vec3(0.0);
    for (int j = 0; j < 6; j++) for (int i = 0; i < 8; i++) {
      vec2 l = (vec2(float(i), float(j)) + 0.5) / vec2(8.0, 6.0);
      s += texture2D(uAtlas, (cell + l) / ${CELLS}.0).rgb;
    }
    gl_FragColor = vec4(s / 48.0, 1.0);
  }
`;

const VERTEX = `
  varying vec2 vUv;
  varying vec4 vClip;
  void main() {
    vUv = uv;
    vec4 clip = vec4(position.xy, 0.0, 1.0);
    vClip = clip;
    gl_Position = clip;
  }
`;

// The room: off + diff x (what the source shows). highp throughout: the
// homography of a far screen is a few pixels wide and half precision would
// smear it.
const ROOM_FRAGMENT = `
  precision highp float;
  uniform sampler2D uOff;
  uniform sampler2D uDiff;
  uniform sampler2D uIds;
  uniform sampler2D uH;          // 3 x 32: the inverse homography of source id, one row per texel
  uniform sampler2D uSrc;        // 32 x 2: row 0 (cell, gain, isScreen, 0), row 1 tint rgb
  uniform sampler2D uAtlas;
  uniform sampler2D uMean;       // 4 x 4
  uniform sampler2D uText;
  uniform float uHasText;
  uniform float uLow;            // indirect light gain
  uniform float uFlicker;        // CRT flicker amplitude
  uniform float uTime;
  uniform float uGrid;           // ?arcadegrid=1
  uniform float uDropWhite;      // marquees white on the drop
  uniform vec2 uUvScale;
  uniform vec2 uUvOffset;
  varying vec2 vUv;
  varying vec4 vClip;
${SAFE_ZONE_VERTEX_CHUNK}

  vec3 hrow(float id, float r) { return texture2D(uH, vec2((r + 0.5) / 3.0, (id + 0.5) / ${MAX_SOURCES}.0)).rgb; }
  vec2 localOf(float id, vec2 uv) {
    vec3 q = vec3(dot(hrow(id, 0.0), vec3(uv, 1.0)), dot(hrow(id, 1.0), vec3(uv, 1.0)), dot(hrow(id, 2.0), vec3(uv, 1.0)));
    return q.xy / q.z;
  }
  vec4 srcOf(float id, float row) { return texture2D(uSrc, vec2((id + 0.5) / ${MAX_SOURCES}.0, row > 0.5 ? 0.75 : 0.25)); }
  vec2 cellUv(float cell, vec2 l) {
    vec2 cxy = vec2(mod(cell, ${CELLS}.0), floor(cell / ${CELLS}.0));
    return (cxy + clamp(l, 0.0, 1.0)) / ${CELLS}.0;
  }

  void main() {
    vec2 uv = vUv * uUvScale + uUvOffset;
    vec4 idc = texture2D(uIds, uv);
    float id = floor(idc.r * 255.0 + 0.5);
    float kind = floor(idc.g * 255.0 + 0.5);
    vec3 off = texture2D(uOff, uv).rgb;
    vec3 diff = texture2D(uDiff, uv).rgb;
    vec4 s0 = srcOf(id, 0.0);
    vec3 light = vec3(0.0);

    if (kind > 254.5) {
      // A SCREEN: its (u, v), a little barrel, scanlines, vignette, flicker
      vec2 l = localOf(id, uv);
      vec2 lc = l * 2.0 - 1.0;
      lc *= 1.0 + 0.05 * dot(lc, lc);
      vec2 lcrt = lc * 0.5 + 0.5;
      float inside = step(0.0, lcrt.x) * step(lcrt.x, 1.0) * step(0.0, lcrt.y) * step(lcrt.y, 1.0);
      vec3 content = texture2D(uAtlas, cellUv(s0.r, lcrt)).rgb;
      float scan = 0.90 + 0.10 * sin(lcrt.y * 48.0 * 6.28318);
      float vig = 1.0 - 0.35 * dot(lc, lc) * dot(lc, lc);
      float flick = 1.0 + uFlicker * sin(uTime * 83.0 + id * 7.0);
      if (uGrid > 0.5) {
        vec2 g = abs(fract(l * vec2(8.0, 6.0)) - 0.5);
        float line = step(0.44, max(g.x, g.y));
        float edge = step(l.x, 0.02) + step(0.98, l.x) + step(l.y, 0.02) + step(0.98, l.y);
        float insideL = step(0.0, l.x) * step(l.x, 1.0) * step(0.0, l.y) * step(l.y, 1.0);
        content = mix(vec3(0.05, 0.15, 0.05), vec3(1.0), line * 0.5 + min(edge, 1.0)) * insideL;
        inside = 1.0; scan = 1.0; vig = 1.0; flick = 1.0;
      }
      light = diff * (content * 1.45 * scan * vig * flick * inside + vec3(0.02) * (1.0 - inside));
    } else if (kind > 199.5) {
      // A MARQUEE: its tint, the chase gain, the game's name
      vec4 s1 = srcOf(id, 1.0);
      vec3 tint = mix(s1.rgb, vec3(1.0), uDropWhite);
      vec2 l = localOf(id, uv);
      float t = 0.0;
      if (uHasText > 0.5) {
        float strip = ${STRIP_NAMES}.0 + floor(s0.a / 2.0);
        float hf = mod(s0.a, 2.0);   // 'half' is reserved in GLSL
        vec2 tl = vec2(l.x, (l.y - 0.2) / 0.6);
        float insideL = step(0.0, tl.x) * step(tl.x, 1.0) * step(0.0, tl.y) * step(tl.y, 1.0);
        t = texture2D(uText, vec2(hf * 0.5 + clamp(tl.x, 0.0, 1.0) * 0.5, (strip + 1.0 - clamp(tl.y, 0.0, 1.0)) / 16.0)).r * insideL;
      }
      light = diff * tint * s0.g * (1.0 - 0.75 * t);
    } else {
      // INDIRECT LIGHT: the nearest source's colour on the cabinet
      float w = kind / 64.0;
      vec3 srcCol;
      if (s0.b > 0.5) {
        vec2 cxy = vec2(mod(s0.r, ${CELLS}.0), floor(s0.r / ${CELLS}.0));
        vec3 m = texture2D(uMean, (cxy + 0.5) / ${CELLS}.0).rgb;
        float lum = dot(m, vec3(0.2126, 0.7152, 0.0722));
        srcCol = m / max(lum, 0.12) * 0.75;
      } else {
        vec4 s1 = srcOf(id, 1.0);
        srcCol = mix(s1.rgb, vec3(1.0), uDropWhite) * s0.g;
      }
      light = diff * srcCol * (0.35 + 0.65 * w) * uLow;
    }

    float att = safeZoneAttenuation(vClip);
    att = mix(${SAFE_FLOOR.toFixed(2)}, 1.0, clamp((att - 0.32) / 0.68, 0.0, 1.0));
    gl_FragColor = vec4(off + light * att, 1.0);
  }
`;

function urlParam(name) {
  if (typeof window === 'undefined' || !window.location) return null;
  return new URLSearchParams(window.location.search).get(name);
}
function clamp01(v) { return v < 0 ? 0 : (v > 1 ? 1 : v); }

// --- the text canvas ----------------------------------------------------------------
function drawText(ctx, text, x0, y0, scale, maxWidth) {
  const chars = text.toUpperCase().split('');
  let w = chars.length * 6 * scale - scale;
  while (w > maxWidth && scale > 1) { scale--; w = chars.length * 6 * scale - scale; }
  let x = x0 - w / 2;
  for (const ch of chars) {
    const g = FONT[ch] || FONT[' '];
    for (let r = 0; r < 7; r++) for (let c = 0; c < 5; c++) if (g[r] & (1 << (4 - c))) ctx.fillRect(x + c * scale, y0 + r * scale, scale, scale);
    x += 6 * scale;
  }
}

export class ArcadeVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;
    this._layout = TIER[this._tier];
    // ?arcadegrid=1 draws a grid in every screen's own coordinates.
    this._grid = urlParam('arcadegrid') === '1';
    // ?arcadegame=bricks|shooter|snake|blocks|portrait|attract on every screen.
    const g = urlParam('arcadegame');
    this._forcedGame = g != null && MODE_NAMES[g] !== undefined ? MODE_NAMES[g] : null;
    // ?arcadesync=1 fires the drop's synchronisation at start and every 8 bars.
    this._debugSync = urlParam('arcadesync') === '1';
    this._probe = urlParam('arcadeprobe') === '1';
    this._postProcessing = { bloomStrength: 0.45, vignette: 0.3, aberration: 0.0015 };
  }

  get postProcessing() { return this._postProcessing; }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    this._scratch = new THREE.Color();
    this._hsl = { h: 0, s: 0, l: 0 };
    const assets = getArcade();
    this._assets = assets;
    if (!assets) {
      const self = this;
      preloadArcade().then(function (a) {
        if (!a || !self._scene || self._assets) return;
        const s = self._scene, c = self._camera;
        self.dispose();
        self.init(s, c);
      });
    }
    const data = assets ? assets.data : { size: [1376, 768], screens: [], marquees: [] };
    this._imageAspect = data.size[0] / data.size[1];
    this._screens = data.screens;
    this._marquees = data.marquees;
    const S = this._screens.length;

    // --- the homographies and the per-source data, in textures
    this._hData = new Float32Array(3 * MAX_SOURCES * 4);
    const put = (src) => {
      if (src.id >= MAX_SOURCES) return;
      for (let r = 0; r < 3; r++) { const o = (src.id * 3 + r) * 4; this._hData[o] = src.hinv[r * 3]; this._hData[o + 1] = src.hinv[r * 3 + 1]; this._hData[o + 2] = src.hinv[r * 3 + 2]; this._hData[o + 3] = 1; }
    };
    for (const s of this._screens) put(s);
    for (const m of this._marquees) put(m);
    this._hTex = new THREE.DataTexture(this._hData, 3, MAX_SOURCES, THREE.RGBAFormat, THREE.FloatType);
    this._hTex.minFilter = this._hTex.magFilter = THREE.NearestFilter;
    this._hTex.generateMipmaps = false;
    this._hTex.needsUpdate = true;
    this._srcData = new Float32Array(MAX_SOURCES * 2 * 4);
    this._srcTex = new THREE.DataTexture(this._srcData, MAX_SOURCES, 2, THREE.RGBAFormat, THREE.FloatType);
    this._srcTex.minFilter = this._srcTex.magFilter = THREE.NearestFilter;
    this._srcTex.generateMipmaps = false;
    this._cellsData = new Float32Array(16 * 4);
    this._cellsTex = new THREE.DataTexture(this._cellsData, 16, 1, THREE.RGBAFormat, THREE.FloatType);
    this._cellsTex.minFilter = this._cellsTex.magFilter = THREE.NearestFilter;
    this._cellsTex.generateMipmaps = false;
    this._paletteData = new Float32Array(PALETTE_SIZE * 4);
    this._paletteTex = new THREE.DataTexture(this._paletteData, PALETTE_SIZE, 1, THREE.RGBAFormat, THREE.FloatType);
    this._paletteTex.minFilter = this._paletteTex.magFilter = THREE.NearestFilter;
    this._paletteTex.generateMipmaps = false;

    // --- the games: one per screen, a seed and a beat offset each
    this._games = new Array(S).fill(MODE_ATTRACT);
    this._seeds = new Float32Array(S);
    this._offsets = new Float32Array(S);
    for (let i = 0; i < S; i++) { this._seeds[i] = Math.random(); this._offsets[i] = Math.random(); }
    this._marqueeTint = this._marquees.map(() => new THREE.Color(1, 0.9, 0.7));
    this._marqueeGain = new Float32Array(this._marquees.length).fill(1);
    this._names = this._marquees.map(() => this._makeName());
    // the chase rows: left row front to back, right row front to back
    const left = this._screens.filter((s) => s.cx < 0.5).sort((a, b) => a.cx - b.cx).map((s) => s.id);
    const right = this._screens.filter((s) => s.cx >= 0.5).sort((a, b) => b.cx - a.cx).map((s) => s.id);
    this._rows = [left, right];
    this._dealGames();

    // --- the cover, quantised to sixteen colours, and the text canvas
    this._coverTex = null;
    this._textTex = null;
    this._buildTextCanvas();
    this._makeFallbackCover();

    // --- the atlas and its passes
    const [aw, ah] = this._layout.atlas;
    const rt = () => new THREE.WebGLRenderTarget(aw, ah, { minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, depthBuffer: false, stencilBuffer: false });
    this._atlasRT = rt();
    this._persistRT = this._layout.persistence ? [rt(), rt()] : null;
    this._persistIx = 0;
    this._meanRT = new THREE.WebGLRenderTarget(CELLS, CELLS, { minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, depthBuffer: false, stencilBuffer: false });
    this._passScene = new THREE.Scene();
    this._passCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    this._passGeo = new THREE.PlaneGeometry(2, 2);
    this._atlasUniforms = {
      uCells: { value: this._cellsTex }, uCover: { value: this._coverTex }, uPalette: { value: this._paletteTex }, uText: { value: this._textTex },
      uBeat: { value: 0 }, uBeatPhase: { value: 0 }, uBar: { value: 0 }, uBarPhase: { value: 0 }, uBlink: { value: 1 }, uDropBar: { value: -1 }, uHasText: { value: this._textTex ? 1 : 0 }
    };
    this._atlasMat = new THREE.ShaderMaterial({ uniforms: this._atlasUniforms, vertexShader: PASS_VERTEX, fragmentShader: ATLAS_FRAGMENT, depthTest: false, depthWrite: false });
    this._persistMat = new THREE.ShaderMaterial({ uniforms: { uFresh: { value: null }, uPrev: { value: null } }, vertexShader: PASS_VERTEX, fragmentShader: PERSIST_FRAGMENT, depthTest: false, depthWrite: false });
    this._meanMat = new THREE.ShaderMaterial({ uniforms: { uAtlas: { value: null } }, vertexShader: PASS_VERTEX, fragmentShader: MEAN_FRAGMENT, depthTest: false, depthWrite: false });
    this._passQuad = new THREE.Mesh(this._passGeo, this._atlasMat);
    this._passQuad.frustumCulled = false;
    this._passScene.add(this._passQuad);
    this._frame = 0;

    // --- the room
    this._uniforms = Object.assign({
      uOff: { value: assets ? assets.off : null }, uDiff: { value: assets ? assets.diff : null }, uIds: { value: assets ? assets.ids : null },
      uH: { value: this._hTex }, uSrc: { value: this._srcTex }, uAtlas: { value: this._atlasRT.texture }, uMean: { value: this._meanRT.texture },
      uText: { value: this._textTex }, uHasText: { value: this._textTex ? 1 : 0 },
      uLow: { value: 1 }, uFlicker: { value: 0 }, uTime: { value: 0 }, uGrid: { value: this._grid ? 1 : 0 }, uDropWhite: { value: 0 },
      uUvScale: { value: new THREE.Vector2(1, 1) }, uUvOffset: { value: new THREE.Vector2(0, 0) }
    }, safeZoneUniforms);
    this._material = new THREE.ShaderMaterial({ uniforms: this._uniforms, vertexShader: VERTEX, fragmentShader: ROOM_FRAGMENT, depthTest: false, depthWrite: false });
    this._geometry = new THREE.PlaneGeometry(2, 2);
    this._quad = new THREE.Mesh(this._geometry, this._material);
    this._quad.frustumCulled = false;
    this._quad.renderOrder = -10;
    const self = this;
    this._quad.onBeforeRender = function (renderer) { self._renderAtlas(renderer); };
    scene.add(this._quad);

    this._time = 0;
    this._beat = 0;
    this._bar = 0;
    this._sinceStep = 0;
    this._beatPeriod = 60 / SYNTH_BPM;
    this._lastBeatPhase = null;
    this._lastBarPhase = null;
    this._lastPhrasePhase = null;
    this._lastMode = null;
    this._beatPhase = 0;
    this._barPhase = 0;
    this._dropBar = -1;
    this._dropBarsLeft = 0;
    this._paused = false;
    this._chase = 0;
    this._bands = { low: 0.5, mid: 0.5, high: 0.5 };
    this._synth = { low: SYNTH_BASE, mid: SYNTH_BASE, high: SYNTH_BASE };
    this._synthPhase = { low: null, mid: null, high: null };
    this._lastPaletteRef = undefined;
    this._artUrl = undefined;
    this._title = '';
    this._aspect = 0;
    this._applyPalette(null);
    this._reframe();
    this._writeCells();
    this._writeSources();
    if (this._debugSync) this._onDrop();
  }

  _makeName() {
    return WORDS_A[Math.floor(Math.random() * WORDS_A.length)] + ' ' + WORDS_B[Math.floor(Math.random() * WORDS_B.length)];
  }

  // A new distribution of the games over the screens, ATTRACT on a couple.
  _dealGames() {
    const S = this._screens.length;
    const pool = GAME_POOL.slice();
    for (let i = pool.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); const t = pool[i]; pool[i] = pool[j]; pool[j] = t; }
    for (let i = 0; i < S; i++) this._games[i] = this._forcedGame != null ? this._forcedGame : pool[i % pool.length];
  }

  _rotateGames() {
    if (this._forcedGame != null || this._games.length < 2) return;
    const last = this._games[this._games.length - 1];
    for (let i = this._games.length - 1; i > 0; i--) this._games[i] = this._games[i - 1];
    this._games[0] = last;
  }

  // --- text: the title and the game names, in the pixel font ---------------------------
  _buildTextCanvas() {
    if (typeof document === 'undefined') return;
    const cv = document.createElement('canvas');
    cv.width = 1024; cv.height = 1024;
    this._textCanvas = cv;
    this._textTex = new THREE.CanvasTexture(cv);
    this._textTex.minFilter = THREE.LinearFilter;
    this._textTex.magFilter = THREE.NearestFilter;
    this._textTex.generateMipmaps = false;
    this._textTex.colorSpace = THREE.NoColorSpace;
    this._textTex.flipY = false;
    this._redrawText();
  }

  _redrawText() {
    if (!this._textCanvas) return;
    const ctx = this._textCanvas.getContext('2d');
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, 1024, 1024);
    ctx.fillStyle = '#fff';
    const strip = (s, text, scale, maxW) => drawText(ctx, text, 512, s * 64 + 4, scale, maxW);
    strip(STRIP_TITLE, this._title || 'PARTY MODE', 8, 1000);
    strip(STRIP_COIN, 'INSERT COIN', 8, 1000);
    strip(STRIP_OVER, 'GAME OVER', 8, 1000);
    strip(STRIP_LEVEL, 'LEVEL UP', 8, 1000);
    strip(STRIP_PAUSED, 'PAUSED', 8, 1000);
    for (let i = 0; i < this._names.length && i < 22; i++) {
      const s = STRIP_NAMES + Math.floor(i / 2), half = i % 2;
      drawText(ctx, this._names[i], half * 512 + 256, s * 64 + 10, 6, 490);
    }
    this._textTex.needsUpdate = true;
  }

  // --- the cover: quantised to sixteen colours on the CPU ---------------------------
  _makeFallbackCover() {
    // sixteen flat colour blocks from the fallback palette, so the games
    // play before any artwork lands
    const data = new Uint8Array(COVER_PX * COVER_PX * 4);
    for (let y = 0; y < COVER_PX; y++) for (let x = 0; x < COVER_PX; x++) {
      const i = (Math.floor(x / 16) + Math.floor(y / 16) * 4) % FALLBACK_COLORS.length;
      this._scratch.set(FALLBACK_COLORS[i]);
      const o = (y * COVER_PX + x) * 4;
      data[o] = this._scratch.r * 255; data[o + 1] = this._scratch.g * 255; data[o + 2] = this._scratch.b * 255; data[o + 3] = 255;
    }
    this._setCover(data);
  }

  _setCover(rgba) {
    // the palette: a few rounds of k-means on the pixels, then the
    // quantised picture
    const n = COVER_PX * COVER_PX;
    const K = PALETTE_SIZE;
    const cent = [];
    for (let k = 0; k < K; k++) { const i = Math.floor((k + 0.5) * n / K); cent.push([rgba[i * 4], rgba[i * 4 + 1], rgba[i * 4 + 2]]); }
    const assign = new Uint8Array(n);
    for (let it = 0; it < 6; it++) {
      const acc = new Float64Array(K * 4);
      for (let i = 0; i < n; i++) {
        let best = 0, bd = Infinity;
        for (let k = 0; k < K; k++) { const dr = rgba[i * 4] - cent[k][0], dg = rgba[i * 4 + 1] - cent[k][1], db = rgba[i * 4 + 2] - cent[k][2]; const d = dr * dr + dg * dg + db * db; if (d < bd) { bd = d; best = k; } }
        assign[i] = best;
        acc[best * 4] += rgba[i * 4]; acc[best * 4 + 1] += rgba[i * 4 + 1]; acc[best * 4 + 2] += rgba[i * 4 + 2]; acc[best * 4 + 3]++;
      }
      for (let k = 0; k < K; k++) if (acc[k * 4 + 3] > 0) cent[k] = [acc[k * 4] / acc[k * 4 + 3], acc[k * 4 + 1] / acc[k * 4 + 3], acc[k * 4 + 2] / acc[k * 4 + 3]];
    }
    // palette sorted by luminance: index 0 darkest, 1 brightest, then the rest
    const lum = (c) => 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
    const order = cent.map((c, k) => k).sort((a, b) => lum(cent[a]) - lum(cent[b]));
    const sorted = [cent[order[0]], cent[order[K - 1]]];
    for (let i = 1; i < K - 1; i++) sorted.push(cent[order[i]]);
    const remap = new Uint8Array(K);
    remap[order[0]] = 0; remap[order[K - 1]] = 1;
    for (let i = 1; i < K - 1; i++) remap[order[i]] = i + 1;
    for (let k = 0; k < K; k++) { const c = sorted[k]; this._paletteData[k * 4] = Math.pow(c[0] / 255, 2.2); this._paletteData[k * 4 + 1] = Math.pow(c[1] / 255, 2.2); this._paletteData[k * 4 + 2] = Math.pow(c[2] / 255, 2.2); this._paletteData[k * 4 + 3] = 1; }
    // the brightest palette entry drives sprites: keep it vivid
    this._paletteTex.needsUpdate = true;
    const q = new Uint8Array(n * 4);
    for (let i = 0; i < n; i++) { const c = sorted[remap[assign[i]]]; q[i * 4] = c[0]; q[i * 4 + 1] = c[1]; q[i * 4 + 2] = c[2]; q[i * 4 + 3] = 255; }
    if (this._coverTex) this._coverTex.dispose();
    this._coverTex = new THREE.DataTexture(q, COVER_PX, COVER_PX, THREE.RGBAFormat, THREE.UnsignedByteType);
    this._coverTex.colorSpace = THREE.SRGBColorSpace;
    this._coverTex.minFilter = this._coverTex.magFilter = THREE.NearestFilter;
    this._coverTex.generateMipmaps = false;
    this._coverTex.flipY = true;
    this._coverTex.needsUpdate = true;
    if (this._atlasUniforms) this._atlasUniforms.uCover.value = this._coverTex;
  }

  _loadCover(url) {
    if (typeof document === 'undefined') return;
    const self = this;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = function () {
      if (self._artUrl !== url || !self._scene) return;
      const cv = document.createElement('canvas');
      cv.width = COVER_PX; cv.height = COVER_PX;
      const ctx = cv.getContext('2d');
      ctx.drawImage(img, 0, 0, COVER_PX, COVER_PX);
      let px;
      try { px = ctx.getImageData(0, 0, COVER_PX, COVER_PX).data; } catch (e) { return; }
      self._setCover(new Uint8Array(px.buffer.slice(0)));
    };
    img.src = artProxyUrl(url);
  }

  // --- the frame and the palette --------------------------------------------------
  _reframe() {
    const aspect = safeZoneUniforms.uAspect.value || 16 / 9;
    this._aspect = aspect;
    const u = this._uniforms;
    if (aspect > this._imageAspect) { const k = this._imageAspect / aspect; u.uUvScale.value.set(1, k); u.uUvOffset.value.set(0, (1 - k) / 2); }
    else { const k = aspect / this._imageAspect; u.uUvScale.value.set(k, 1); u.uUvOffset.value.set((1 - k) / 2, 0); }
  }

  _applyPalette(palette) {
    const vivid = palette && palette.vividColors && palette.vividColors.length ? palette.vividColors : FALLBACK_COLORS;
    for (let i = 0; i < this._marquees.length; i++) {
      this._scratch.set(vivid[i % vivid.length]).getHSL(this._hsl);
      // marquees stay near white: a light, desaturated tint of the palette
      this._marqueeTint[i].setHSL(this._hsl.h, 0.45, 0.78);
    }
    if (!palette || !palette.vividColors) return;
  }

  // --- the tables -----------------------------------------------------------------------
  _writeCells() {
    const d = this._cellsData;
    for (let i = 0; i < 16; i++) {
      const s = i < this._games.length;
      let mode = s ? this._games[i] : MODE_ATTRACT;
      if (this._paused) mode = MODE_PAUSED;
      else if (this._dropBar >= 0) mode = MODE_SYNC;
      d[i * 4] = mode; d[i * 4 + 1] = s ? this._seeds[i] : 0.5; d[i * 4 + 2] = s ? this._offsets[i] : 0; d[i * 4 + 3] = 0;
    }
    this._cellsTex.needsUpdate = true;
  }

  _writeSources() {
    const d = this._srcData;
    for (const s of this._screens) {
      if (s.id >= MAX_SOURCES) continue;
      const o = s.id * 4;
      d[o] = s.id - 1; d[o + 1] = 1; d[o + 2] = 1; d[o + 3] = 0;
    }
    for (let i = 0; i < this._marquees.length; i++) {
      const m = this._marquees[i];
      if (m.id >= MAX_SOURCES) continue;
      const o = m.id * 4;
      d[o] = 0; d[o + 1] = this._marqueeGain[i]; d[o + 2] = 0; d[o + 3] = i;
      const c = this._marqueeTint[i];
      const o1 = (MAX_SOURCES + m.id) * 4;
      d[o1] = c.r; d[o1 + 1] = c.g; d[o1 + 2] = c.b; d[o1 + 3] = 1;
    }
    this._srcTex.needsUpdate = true;
  }

  // The marquee chase: along each row, one cabinet per beat; the others
  // glow at 40%. On the drop, everything.
  _stepChase(dt) {
    const target = new Float32Array(this._marquees.length);
    for (let i = 0; i < this._marquees.length; i++) {
      const sid = this._marquees[i].screen;
      let lit = 0.4;
      for (const row of this._rows) { const k = row.indexOf(sid); if (k >= 0 && row.length && (this._beat % row.length) === k) lit = 1; }
      if (this._dropBar >= 0) lit = 1;
      target[i] = lit;
    }
    for (let i = 0; i < this._marquees.length; i++) {
      const g = this._marqueeGain[i];
      this._marqueeGain[i] = target[i] > g ? target[i] : g + (target[i] - g) * Math.min(1, dt / 0.18);
    }
  }

  // --- the atlas, once per frame -----------------------------------------------------
  _renderAtlas(renderer) {
    this._frame++;
    if (this._layout.every > 1 && this._frame % this._layout.every) return;
    const prev = renderer.getRenderTarget();
    this._passQuad.material = this._atlasMat;
    renderer.setRenderTarget(this._atlasRT);
    renderer.render(this._passScene, this._passCam);
    let shown = this._atlasRT.texture;
    if (this._persistRT) {
      const cur = this._persistRT[this._persistIx], last = this._persistRT[1 - this._persistIx];
      this._persistMat.uniforms.uFresh.value = this._atlasRT.texture;
      this._persistMat.uniforms.uPrev.value = last.texture;
      this._passQuad.material = this._persistMat;
      renderer.setRenderTarget(cur);
      renderer.render(this._passScene, this._passCam);
      shown = cur.texture;
      this._persistIx = 1 - this._persistIx;
    }
    this._meanMat.uniforms.uAtlas.value = shown;
    this._passQuad.material = this._meanMat;
    renderer.setRenderTarget(this._meanRT);
    renderer.render(this._passScene, this._passCam);
    renderer.setRenderTarget(prev);
    this._uniforms.uAtlas.value = shown;
  }

  // --- the rhythm ----------------------------------------------------------------------
  _onBeat() {
    this._beat++;
    this._sinceStep = 0;
  }
  _onBar() {
    this._bar++;
    if (this._dropBar >= 0) { this._dropBar++; if (this._dropBar >= DROP_BARS) this._dropBar = -1; }
    else this._rotateGames();
    if (this._debugSync && this._dropBar < 0 && this._bar % 8 === 0) this._onDrop();
  }
  _onPhrase() { if (this._dropBar < 0) this._dealGames(); }
  _onDrop() { this._dropBar = 0; }

  _stepSynthBands(deltaTime, profile) {
    const k = Math.exp(-SYNTH_DECAY * deltaTime);
    const sy = this._synth;
    sy.low = SYNTH_BASE + (sy.low - SYNTH_BASE) * k;
    sy.mid = SYNTH_BASE + (sy.mid - SYNTH_BASE) * k;
    sy.high = SYNTH_BASE + (sy.high - SYNTH_BASE) * k;
    const fired = (key, phase) => { const prev = this._synthPhase[key]; this._synthPhase[key] = phase; return prev != null && phase < prev; };
    const bar = profile.barPhase, beat = profile.beatPhase;
    if (bar != null && beat != null) {
      if (fired('low', beat)) sy.low = 1.0;
      if (fired('mid', (bar + 0.5) % 1)) sy.mid = 0.85;
      if (fired('high', (beat + 0.5) % 1)) sy.high = 0.75;
      return;
    }
    const t = this._time;
    if (fired('low', (t / SYNTH_PERIOD_LOW) % 1)) sy.low = 1.0;
    if (fired('mid', (t / SYNTH_PERIOD_MID) % 1)) sy.mid = 0.85;
    if (fired('high', (t / SYNTH_PERIOD_HIGH) % 1)) sy.high = 0.75;
  }
  _effectiveBands(director) {
    if (director && director.hasReliableGrid && director.bands) return director.bands;
    return this._synth;
  }

  update(deltaTime, profile) {
    const dt = Math.min(deltaTime, 0.05);
    const playing = profile.playbackState === 'PLAYING';
    const director = profile.director;
    const u = this._uniforms;

    if (profile.artworkUrl !== this._artUrl) {
      this._artUrl = profile.artworkUrl;
      if (profile.artworkUrl) this._loadCover(profile.artworkUrl); else this._makeFallbackCover();
    }
    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      this._applyPalette(profile.artworkPalette);
    }
    const title = (profile.currentTrack && profile.currentTrack.title) || profile.title || '';
    if (title !== this._title) { this._title = title; this._redrawText(); }
    const aspect = safeZoneUniforms.uAspect.value || 0;
    if (aspect && aspect !== this._aspect) this._reframe();

    if (!playing) {
      if (!this._paused) { this._paused = true; this._writeCells(); }
      this._time += dt;
      this._atlasUniforms.uBlink.value = 1;
      u.uTime.value = this._time % 64;
      return;
    }
    if (this._paused) { this._paused = false; this._writeCells(); }

    this._time += dt;
    this._sinceStep += dt;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || SYNTH_BPM;
    this._beatPeriod = 60 / bpm;
    this._stepSynthBands(dt, profile);
    const bands = this._effectiveBands(director);
    this._bands.low = bands && bands.low != null ? bands.low : 0.5;
    this._bands.high = bands && bands.high != null ? bands.high : 0.5;

    const beatPhase = director && director.beat != null ? director.beat : profile.beatPhase;
    let beatEdge = false, barEdge = false, phraseEdge = false;
    if (beatPhase != null) {
      if (this._lastBeatPhase != null && beatPhase < this._lastBeatPhase) beatEdge = true;
      this._lastBeatPhase = beatPhase;
      this._beatPhase = beatPhase;
    } else if (this._sinceStep >= this._beatPeriod) { beatEdge = true; this._beatPhase = 0; }
    else this._beatPhase = clamp01(this._sinceStep / this._beatPeriod);
    const barPhase = director && director.bar != null ? director.bar : profile.barPhase;
    if (barPhase != null) {
      if (this._lastBarPhase != null && barPhase < this._lastBarPhase) barEdge = true;
      this._lastBarPhase = barPhase;
      this._barPhase = barPhase;
    } else { if (beatEdge && this._beat % 4 === 3) barEdge = true; this._barPhase = ((this._beat % 4) + this._beatPhase) / 4; }
    const phrasePhase = director && director.phrase != null ? director.phrase : profile.phrasePhase;
    if (phrasePhase != null) {
      if (this._lastPhrasePhase != null && phrasePhase < this._lastPhrasePhase) phraseEdge = true;
      this._lastPhrasePhase = phrasePhase;
    } else if (beatEdge && this._beat % 16 === 15) phraseEdge = true;
    const mode = director ? director.mode : null;
    if (mode === 'drop' && this._lastMode !== 'drop' && this._dropBar < 0) this._onDrop();
    this._lastMode = mode;

    let changed = false;
    if (beatEdge) { this._onBeat(); changed = true; }
    if (barEdge) { this._onBar(); changed = true; }
    if (phraseEdge) { this._onPhrase(); changed = true; }
    if (changed) this._writeCells();

    this._stepChase(dt);
    this._writeSources();

    const au = this._atlasUniforms;
    au.uBeat.value = this._beat % 4096;
    au.uBeatPhase.value = this._beatPhase;
    au.uBar.value = this._bar % 1024;
    au.uBarPhase.value = this._barPhase;
    au.uBlink.value = this._beatPhase < 0.5 ? 1 : 0;
    au.uDropBar.value = this._dropBar >= 0 ? this._dropBar + this._barPhase : -1;
    u.uLow.value = 0.6 + 0.8 * this._bands.low;
    u.uFlicker.value = 0.08 * this._bands.high;
    u.uDropWhite.value = this._dropBar >= 0 ? 1 : 0;
    u.uTime.value = this._time % 64;

    if (this._probe && typeof window !== 'undefined') window.__arcade = this.getProbe();
  }

  getProbe() {
    return {
      t: this._time, beat: this._beat, bar: this._bar, dropBar: this._dropBar, paused: this._paused,
      games: Array.from(this._games), cells: Array.from({ length: 16 }, (_, i) => this._cellsData[i * 4]),
      marqueeGain: Array.from(this._marqueeGain).map((v) => +v.toFixed(2)), names: this._names.slice(),
      screens: this._screens.length, marquees: this._marquees.length
    };
  }

  dispose() {
    if (this._quad) { this._scene.remove(this._quad); this._quad.onBeforeRender = null; }
    for (const g of [this._geometry, this._passGeo]) if (g) g.dispose();
    for (const m of [this._material, this._atlasMat, this._persistMat, this._meanMat]) if (m) m.dispose();
    for (const t of [this._hTex, this._srcTex, this._cellsTex, this._paletteTex, this._coverTex, this._textTex]) if (t) t.dispose();
    if (this._atlasRT) this._atlasRT.dispose();
    if (this._persistRT) for (const r of this._persistRT) r.dispose();
    if (this._meanRT) this._meanRT.dispose();
    this._quad = null;
    if (typeof window !== 'undefined' && window.__arcade) delete window.__arcade;
  }
}
